import type { Severity } from '../types';

const severityStyles: Record<Severity, string> = {
  critical: 'bg-red-100 text-red-700 ring-red-200',
  high: 'bg-orange-100 text-orange-700 ring-orange-200',
  medium: 'bg-amber-100 text-amber-700 ring-amber-200',
  low: 'bg-green-100 text-green-700 ring-green-200',
};

interface SeverityBadgeProps {
  severity: Severity | 'all';
  interactive?: boolean;
  active?: boolean;
  onClick?: () => void;
}

export function SeverityBadge({
  severity,
  interactive = false,
  active = false,
  onClick,
}: SeverityBadgeProps) {
  const label = severity === 'all' ? 'All' : severity.charAt(0).toUpperCase() + severity.slice(1);

  if (interactive) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={`Filter by severity: ${severity}`}
        aria-pressed={active}
        className={[
          'inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ring-1 transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1',
          active
            ? severity === 'all'
              ? 'bg-gray-900 text-white ring-gray-900'
              : `${severityStyles[severity as Severity]} ring-2`
            : 'bg-white text-gray-700 ring-gray-300 hover:bg-gray-50',
        ].join(' ')}
      >
        {label}
      </button>
    );
  }

  if (severity === 'all') {
    return (
      <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-200">
        All
      </span>
    );
  }

  return (
    <span
      className={[
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ring-1 ring-inset',
        severityStyles[severity],
      ].join(' ')}
    >
      {severity}
    </span>
  );
}
